// The backend (remedy-service src/utils/validator.js:43-44) returns HTTP 403 —
// NOT 401 — for a missing / invalid / EXPIRED auth token: `_validateAuthToken`
// throws HTTP_FORBIDDEN in both the jwt-error branch and the `now > expiresIn`
// branch. 401 (HTTP_UNAUTHORIZED) is only thrown by `_validateRole`, i.e. a
// VALID token whose role lacks permission. So a dead/expired session surfaces
// to the client as a 403 carrying one of these two auth messages.
//
// Keep this list in sync with validator.js ERROR_MESSAGE_INVALID_TOKEN /
// ERROR_AUTH_TOKEN_EXPIRED — the match is exact on purpose, so that a *business*
// 403 (Forbidden / image type / employee phone) never force-logs-out the user.
export const AUTH_FAILURE_MESSAGES: readonly string[] = [
  'The access token expired.',
  'Invalid or missing authorization token.',
]

/**
 * True when an API error means the stored session is no longer usable and the
 * user must be force-logged-out. Only fires while the client still *thinks* it
 * is logged in, so a logged-out 401/403 (e.g. wrong password on the login
 * screen) keeps its normal error UX.
 *
 *  - 401: role-mismatch on a valid token (and any future 401) → session unusable
 *  - 403 + auth message: stale / invalid / expired token (the common case)
 *  - 403 + business message: a permission/validation error, NOT a session
 *    problem — left untouched so it doesn't nuke the user's session.
 */
export const isSessionDeadError = (
  status: number | undefined,
  message: unknown,
  isLoggedIn: boolean | undefined
): boolean => {
  if (!isLoggedIn) return false
  if (status === 401) return true
  if (
    status === 403 &&
    typeof message === 'string' &&
    AUTH_FAILURE_MESSAGES.includes(message)
  ) {
    return true
  }
  return false
}
